import { useCallback, useEffect, useState } from 'react'
import { api } from '@/lib/api'
import {
  isIos,
  isStandalone,
  pushSupported,
  registerServiceWorker,
  urlBase64ToUint8Array,
} from '@/lib/push'
import { useToast } from './useToast'

export type PushStatus =
  /** Browser has no Push API at all (or iOS Safari in a normal tab). */
  | 'unsupported'
  /** Supported, but the user must install the PWA first (iOS). */
  | 'needs-install'
  /** The user blocked notifications; only they can undo it, in browser settings. */
  | 'denied'
  | 'subscribed'
  | 'unsubscribed'
  | 'checking'

export interface PushSubscriptionState {
  status: PushStatus
  isBusy: boolean
  enable: () => Promise<void>
  disable: () => Promise<void>
  sendTest: () => Promise<void>
}

export function usePushSubscription(): PushSubscriptionState {
  const toast = useToast()
  const [status, setStatus] = useState<PushStatus>('checking')
  const [isBusy, setBusy] = useState(false)

  const detect = useCallback(async () => {
    if (!pushSupported()) {
      // iOS gained Web Push in 16.4 — but only for a PWA added to the home
      // screen. In a normal Safari tab the APIs are simply absent.
      setStatus(isIos() && !isStandalone() ? 'needs-install' : 'unsupported')
      return
    }
    if (Notification.permission === 'denied') {
      setStatus('denied')
      return
    }
    const registration = await registerServiceWorker()
    if (!registration) {
      setStatus('unsupported')
      return
    }
    const existing = await registration.pushManager.getSubscription()
    setStatus(existing ? 'subscribed' : 'unsubscribed')
  }, [])

  useEffect(() => {
    void detect()
  }, [detect])

  const enable = useCallback(async () => {
    if (!pushSupported()) {
      setStatus(isIos() && !isStandalone() ? 'needs-install' : 'unsupported')
      return
    }
    setBusy(true)
    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        setStatus(permission === 'denied' ? 'denied' : 'unsubscribed')
        toast.error(
          'Notifications are blocked',
          'Allow notifications for this site in your browser settings, then try again.',
        )
        return
      }

      const registration = await registerServiceWorker()
      if (!registration) {
        setStatus('unsupported')
        return
      }

      let subscription = await registration.pushManager.getSubscription()
      if (!subscription) {
        const { key } = await api.push.vapidPublicKey()
        subscription = await registration.pushManager.subscribe({
          // Required by every browser: each push must show a visible notification.
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(key),
        })
      }

      await api.push.subscribe(subscription.toJSON())
      setStatus('subscribed')
      toast.success('Notifications on', 'You will hear about your requests as they progress.')
    } catch (error) {
      toast.fromError(error, 'Could not turn on notifications')
      await detect()
    } finally {
      setBusy(false)
    }
  }, [detect, toast])

  const disable = useCallback(async () => {
    setBusy(true)
    try {
      const registration = await registerServiceWorker()
      const subscription = await registration?.pushManager.getSubscription()
      if (subscription) {
        // Tell the server first: if unsubscribing locally succeeded but the
        // server row survived, it would keep pushing into a dead endpoint.
        await api.push.unsubscribe(subscription.endpoint)
        await subscription.unsubscribe()
      }
      setStatus('unsubscribed')
      toast.success('Notifications off')
    } catch (error) {
      toast.fromError(error, 'Could not turn off notifications')
    } finally {
      setBusy(false)
    }
  }, [toast])

  const sendTest = useCallback(async () => {
    setBusy(true)
    try {
      await api.push.test()
      toast.success('Test sent', 'It should arrive within a few seconds.')
    } catch (error) {
      toast.fromError(error, 'Could not send a test notification')
    } finally {
      setBusy(false)
    }
  }, [toast])

  return { status, isBusy, enable, disable, sendTest }
}
